import { Component, type ErrorInfo, type ReactNode } from "react";
import { I18nContext } from "../i18n/context";
import { browserLocaleGuess, readStoredLocale, type Locale } from "../i18n/locale";
import { uiString } from "../i18n/ui";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

function resolveLocale(ctx: Locale | undefined): Locale {
  return ctx ?? readStoredLocale() ?? browserLocaleGuess();
}

export class ErrorBoundary extends Component<Props, State> {
  static contextType = I18nContext;
  declare context: React.ContextType<typeof I18nContext>;

  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const locale = resolveLocale(this.context?.locale);
      return (
        <div className="error-boundary" role="alert">
          <h1>{uiString(locale, "error.title")}</h1>
          <p>{uiString(locale, "error.body")}</p>
          <pre className="error-boundary__stack">{this.state.error.message}</pre>
          <button
            type="button"
            className="error-boundary__retry"
            onClick={() => window.location.reload()}
          >
            {uiString(locale, "error.retry")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
